import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-base font-semibold whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        // The secondary button (docs/UX-GUIDE.md): white, a hairline border, a soft shadow.
        outline:
          'border-border bg-background shadow-resting hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        // A filter or choice that stays pressed (aria-pressed) takes the accent, as
        // Stripe marks the selected filter. An 8% tint keeps the blue words at 4.5:1;
        // dark mode takes the light info blue, which the darker panels need.
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground aria-pressed:bg-primary/8 aria-pressed:text-primary dark:hover:bg-muted/50 dark:aria-pressed:text-(--tone-info)',
        destructive:
          'bg-destructive/10 text-destructive-strong hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        // Solid red, for the one control that must be unmissable — Stop
        // impersonating. The strong shade keeps the text readable in both themes.
        'destructive-solid':
          'bg-destructive-strong text-background hover:bg-destructive-strong/90 focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // docs/UX-GUIDE.md: buttons are 40px; small ones 32px, only where rows are
        // dense; on a phone every one is at least 44px to tap (globals.css lifts
        // the height; the icon sizes lift the width too). An icon beside words
        // is 16px and one on its own 20px — set here, over whatever the icon
        // says, so every button matches.
        default:
          'h-10 gap-2 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg]:size-4',
        xs: 'h-8 gap-2 px-3 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg]:size-4',
        sm: 'h-8 gap-2 px-3 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg]:size-4',
        lg: 'h-10 gap-2 px-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4 [&_svg]:size-4',
        icon: 'size-10 max-md:size-11 [&_svg]:size-5',
        'icon-xs': 'size-8 max-md:size-11 [&_svg]:size-5',
        'icon-sm': 'size-8 max-md:size-11 [&_svg]:size-5',
        'icon-lg': 'size-10 max-md:size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

/**
 * `loading` is the one saving state (docs/UX-GUIDE.md: save buttons show a
 * loading state and disable while saving): a small spinner before the words,
 * the button disabled and announced as busy.
 */
function Button({
  className,
  variant = 'default',
  size = 'default',
  loading = false,
  disabled,
  children,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & { loading?: boolean }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
